import { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  Plus, 
  CreditCard,
  Wallet,
  Gift,
  Package,
  Euro,
  History,
  ArrowUpCircle,
  ArrowDownCircle,
  MessageCircle,
  Clock,
  CheckCircle2,
  XCircle,
  Pause,
  Play,
  Scissors,
  Sparkles
} from "lucide-react";
import { format, parseISO, differenceInDays } from "date-fns";
import { it } from "date-fns/locale";

export default function CardCliente({ cliente, onUpdate }) {
  const { token } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [servizi, setServizi] = useState([]);
  const [activeTab, setActiveTab] = useState("card");
  
  // Card prepagata principale (vecchio sistema)
  const [cardPrepagata, setCardPrepagata] = useState(null);
  
  // Dialog states
  const [showNuovaCard, setShowNuovaCard] = useState(false);
  const [showStorico, setShowStorico] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  
  // Ricarica inline
  const [ricaricaAmount, setRicaricaAmount] = useState("");
  const [ricaricaNote, setRicaricaNote] = useState("");
  const [ricaricaOperatoreId, setRicaricaOperatoreId] = useState("");
  
  // Form nuova card
  const [nuovaCardForm, setNuovaCardForm] = useState({
    tipo_card: "prepagata",
    nome: "",
    saldo_iniziale: 0,
    servizio_id: "",
    trattamenti_totali: 0,
    data_scadenza: "",
    note: "",
    metodo_pagamento: "contanti",
    operatore_id: "",
    operatore_nome: ""
  });
  // Operatori per il passaggio in cassa
  const [operatori, setOperatori] = useState([]);

  useEffect(() => {
    if (cliente?.id) {
      fetchCards();
      fetchServizi();
      fetchCardPrepagata();
      fetchOperatori();
    }
  }, [cliente?.id]);

  const fetchOperatori = async () => {
    try {
      const response = await axios.get(`${API}/operatori`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Considera solo operatori attivi (se il flag esiste)
      const lista = (response.data || []).filter(o => o.attivo !== false);
      setOperatori(lista);
    } catch (error) {
      // Silenzioso: la validazione avverrà al submit
    }
  };

  const fetchCards = async () => {
    try {
      const response = await axios.get(`${API}/clienti/${cliente.id}/cards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCards(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Fetch vecchia card prepagata (retrocompatibilità)
  const fetchCardPrepagata = async () => {
    try {
      const response = await axios.get(`${API}/clienti/${cliente.id}/card`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCardPrepagata(response.data);
    } catch (error) {
      // Card non esiste ancora
      setCardPrepagata(null);
    }
  };

  const fetchServizi = async () => {
    try {
      const response = await axios.get(`${API}/servizi`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServizi(response.data.filter(s => s.attivo !== false));
    } catch (error) {
    }
  };

  // Memoize le card filtrate per tipo per evitare filtraggi ripetuti nel render
  const cardTrattamenti = useMemo(() => 
    cards.filter(c => c.tipo_card === "trattamenti"), 
    [cards]
  );
  
  const cardAbbonamenti = useMemo(() => 
    cards.filter(c => c.tipo_card === "abbonamento"), 
    [cards]
  );

  const cardRegalo = useMemo(() => 
    cards.filter(c => c.tipo_card === "regalo"), 
    [cards]
  );

  // Crea card prepagata (vecchio sistema)
  const createCardPrepagata = async () => {
    try {
      await axios.post(`${API}/clienti/${cliente.id}/card`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Card prepagata creata!");
      fetchCardPrepagata();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Errore nella creazione della card");
    }
  };

  // Ricarica card prepagata
  const handleRicarica = async () => {
    const amount = parseFloat(ricaricaAmount);
    if (!amount || amount <= 0) {
      toast.error("Inserisci un importo valido");
      return;
    }
    if (!ricaricaOperatoreId) {
      toast.error("Seleziona l'operatore per il passaggio in cassa");
      return;
    }
    const op = operatori.find(o => o.id === ricaricaOperatoreId);
    
    try {
      await axios.post(`${API}/clienti/${cliente.id}/card/ricarica`, {
        importo: amount,
        descrizione: ricaricaNote || `Ricarica €${amount}`,
        metodo_pagamento: "contanti",
        operatore_id: ricaricaOperatoreId,
        operatore_nome: op?.nome || ""
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Card ricaricata di €${amount} (${op?.nome || "operatore"})`);
      setRicaricaAmount("");
      setRicaricaNote("");
      setRicaricaOperatoreId("");
      fetchCardPrepagata();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella ricarica");
    }
  };

  // Crea nuova card avanzata
  const creaCardAvanzata = async () => {
    try {
      if (nuovaCardForm.tipo_card === "trattamenti" && !nuovaCardForm.servizio_id) {
        toast.error("Seleziona un servizio");
        return;
      }
      if (nuovaCardForm.tipo_card === "trattamenti" && nuovaCardForm.trattamenti_totali <= 0) {
        toast.error("Inserisci il numero di trattamenti");
        return;
      }
      // Validazione operatore obbligatorio per passaggio cassa
      if (!nuovaCardForm.operatore_id) {
        toast.error("Seleziona l'operatore per il passaggio in cassa");
        return;
      }
      if (!(parseFloat(nuovaCardForm.saldo_iniziale) > 0)) {
        toast.error("Inserisci l'importo (€) della card per registrare l'incasso in cassa");
        return;
      }
      
      let nome = nuovaCardForm.nome;
      if (!nome) {
        if (nuovaCardForm.tipo_card === "trattamenti") {
          const servizio = servizi.find(s => s.id === nuovaCardForm.servizio_id);
          nome = `Pacchetto ${nuovaCardForm.trattamenti_totali}x ${servizio?.nome || "Trattamento"}`;
        } else if (nuovaCardForm.tipo_card === "regalo") {
          nome = `Gift Card €${nuovaCardForm.saldo_iniziale}`;
        } else {
          nome = `Card Prepagata`;
        }
      }
      
      await axios.post(`${API}/clienti/${cliente.id}/cards`, {
        ...nuovaCardForm,
        nome
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Card creata e incasso registrato in cassa (${nuovaCardForm.operatore_nome})`);
      setShowNuovaCard(false);
      resetForm();
      fetchCards();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    }
  };

  const cambiaStatoCard = async (cardId, nuovoStato) => {
    try {
      await axios.put(`${API}/cards/${cardId}/stato?stato=${nuovoStato}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Stato: ${nuovoStato}`);
      fetchCards();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const inviaRemind = async (card) => {
    try {
      const response = await axios.post(`${API}/cards/${card.id}/remind`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.open(response.data.whatsapp_link, "_blank");
      toast.success("Promemoria inviato!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    }
  };

  const resetForm = () => {
    setNuovaCardForm({
      tipo_card: "prepagata",
      nome: "",
      saldo_iniziale: 0,
      servizio_id: "",
      trattamenti_totali: 0,
      data_scadenza: "",
      note: "",
      metodo_pagamento: "contanti",
      operatore_id: "",
      operatore_nome: ""
    });
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "d MMM yyyy, HH:mm", { locale: it });
    } catch {
      return dateStr;
    }
  };

  const getStatoBadge = (stato) => {
    const badges = {
      attiva: <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Attiva</span>,
      sospesa: <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium flex items-center gap-1"><Pause className="w-3 h-3" /> Sospesa</span>,
      scaduta: <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium flex items-center gap-1"><Clock className="w-3 h-3" /> Scaduta</span>,
      esaurita: <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-xs font-medium flex items-center gap-1"><XCircle className="w-3 h-3" /> Esaurita</span>
    };
    return badges[stato] || <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">{stato}</span>;
  };

  if (loading) {
    return <div className="text-center py-4 text-slate-400">Caricamento...</div>;
  }

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="card" className="gap-2">
            <CreditCard className="w-4 h-4" />
            Card
          </TabsTrigger>
          <TabsTrigger value="trattamenti" className="gap-2">
            <Package className="w-4 h-4" />
            Trattamenti
          </TabsTrigger>
          <TabsTrigger value="regalo" className="gap-2">
            <Gift className="w-4 h-4" />
            Regalo
          </TabsTrigger>
        </TabsList>

        {/* TAB CARD PREPAGATA - Come prima */}
        <TabsContent value="card" className="space-y-4 mt-4">
          {cardPrepagata ? (
            <>
              {/* Saldo Card - Design originale */}
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-6 text-white">
                <p className="text-sm opacity-80">Saldo Disponibile</p>
                <p className="text-4xl font-bold mt-1">€{cardPrepagata.saldo?.toFixed(2) || '0.00'}</p>
                <div className="flex gap-6 mt-4 text-sm">
                  <div>
                    <p className="opacity-70">Totale Ricaricato</p>
                    <p className="font-semibold">€{cardPrepagata.totale_ricaricato?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div>
                    <p className="opacity-70">Totale Speso</p>
                    <p className="font-semibold">€{cardPrepagata.totale_speso?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </div>

              {/* Ricarica - Design originale + Operatore obbligatorio */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <ArrowUpCircle className="w-4 h-4 text-emerald-600" />
                  Ricarica Card
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                  <Input
                    type="number"
                    placeholder="Importo €"
                    value={ricaricaAmount}
                    onChange={(e) => setRicaricaAmount(e.target.value)}
                    min="0"
                    step="0.01"
                    data-testid="input-ricarica-importo"
                  />
                  <Input
                    placeholder="Note (opzionale)"
                    value={ricaricaNote}
                    onChange={(e) => setRicaricaNote(e.target.value)}
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Select
                    value={ricaricaOperatoreId}
                    onValueChange={setRicaricaOperatoreId}
                  >
                    <SelectTrigger className="flex-1 bg-white" data-testid="select-operatore-ricarica">
                      <SelectValue placeholder="Operatore (obbligatorio) *" />
                    </SelectTrigger>
                    <SelectContent>
                      {operatori.length === 0 && (
                        <div className="px-2 py-1 text-xs text-slate-400">Nessun operatore</div>
                      )}
                      {operatori.map(op => (
                        <SelectItem key={op.id} value={op.id}>{op.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    onClick={handleRicarica}
                    disabled={!ricaricaOperatoreId || !(parseFloat(ricaricaAmount) > 0)}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="btn-ricarica-card"
                  >
                    Ricarica
                  </Button>
                </div>
                {/* Quick amounts */}
                <div className="flex gap-2 mt-2">
                  {[10, 20, 50, 100].map(amt => (
                    <Button
                      key={amt}
                      variant="outline"
                      size="sm"
                      onClick={() => setRicaricaAmount(amt.toString())}
                      className="flex-1"
                    >
                      €{amt}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Storico Movimenti - Design originale */}
              <div>
                <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <History className="w-4 h-4 text-slate-500" />
                  Storico Pagamenti
                </h4>
                {cardPrepagata.movimenti && cardPrepagata.movimenti.length > 0 ? (
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {cardPrepagata.movimenti.slice().reverse().slice(0, 15).map((mov, idx) => (
                      <div 
                        key={mov.id || idx}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          mov.tipo === 'ricarica' ? 'bg-green-50' : 'bg-red-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {mov.tipo === 'ricarica' ? (
                            <ArrowUpCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <ArrowDownCircle className="w-5 h-5 text-red-500" />
                          )}
                          <div>
                            <p className="text-sm font-medium text-slate-700">{mov.descrizione}</p>
                            <p className="text-xs text-slate-500">
                              {formatDate(mov.data)}
                              {mov.operatore_nome && (
                                <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-medium">
                                  👤 {mov.operatore_nome}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${
                            mov.tipo === 'ricarica' ? 'text-green-600' : 'text-red-500'
                          }`}>
                            {mov.tipo === 'ricarica' ? '+' : '-'}€{Math.abs(mov.importo).toFixed(2)}
                          </p>
                          <p className="text-xs text-slate-500">Saldo: €{mov.saldo_dopo?.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">Nessun movimento</p>
                )}
              </div>
            </>
          ) : (
            /* Nessuna Card - Crea */
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                <CreditCard className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Nessuna Card Attiva</h3>
              <p className="text-sm text-slate-500 mb-4">
                Crea una card prepagata per questo cliente
              </p>
              <Button 
                onClick={createCardPrepagata}
                className="bg-emerald-600 hover:bg-emerald-700 gap-2"
              >
                <Plus className="w-4 h-4" />
                Crea Card Prepagata
              </Button>
            </div>
          )}
        </TabsContent>

        {/* TAB CARD TRATTAMENTI */}
        <TabsContent value="trattamenti" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">Pacchetti di trattamenti prepagati</p>
            <Button 
              size="sm" 
              onClick={() => {
                setNuovaCardForm({ ...nuovaCardForm, tipo_card: "trattamenti" });
                setShowNuovaCard(true);
              }}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-1" /> Nuovo Pacchetto
            </Button>
          </div>

          {cardTrattamenti.length > 0 ? (
            <div className="space-y-3">
              {cardTrattamenti.map(card => (
                <div 
                  key={card.id}
                  className={`p-4 rounded-xl border-2 ${
                    card.stato === "attiva" ? "bg-purple-50 border-purple-300" : "bg-slate-50 border-slate-300"
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center text-white">
                        <Package className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{card.nome}</p>
                        <p className="text-xs text-slate-500">{card.servizio_nome}</p>
                        {card.operatore_nome && (
                          <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold" title="Operatore che ha venduto la card">
                            👤 {card.operatore_nome}
                          </span>
                        )}
                      </div>
                    </div>
                    {getStatoBadge(card.stato)}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600 flex items-center gap-1">
                        <Scissors className="w-4 h-4" />
                        Trattamenti
                      </span>
                      <span className="font-bold text-purple-700 text-xl">
                        {card.trattamenti_rimanenti} / {card.trattamenti_totali}
                      </span>
                    </div>
                    <Progress 
                      value={(card.trattamenti_rimanenti / card.trattamenti_totali) * 100} 
                      className="h-3" 
                    />
                  </div>

                  {card.data_scadenza && (
                    <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Scade il {format(parseISO(card.data_scadenza), "d MMM yyyy", { locale: it })}
                    </p>
                  )}

                  <div className="flex gap-2 mt-4 pt-3 border-t border-purple-200">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => { setSelectedCard(card); setShowStorico(true); }}
                      className="flex-1"
                    >
                      <History className="w-4 h-4 mr-1" /> Storico
                    </Button>
                    {cliente.telefono && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => inviaRemind(card)}
                        className="text-green-600 border-green-200 hover:bg-green-50"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                    )}
                    {card.stato === "attiva" ? (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => cambiaStatoCard(card.id, "sospesa")}
                        className="text-yellow-600"
                      >
                        <Pause className="w-4 h-4" />
                      </Button>
                    ) : card.stato === "sospesa" && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => cambiaStatoCard(card.id, "attiva")}
                        className="text-green-600"
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-slate-50 rounded-xl">
              <Package className="w-12 h-12 mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500">Nessun pacchetto trattamenti</p>
              <p className="text-xs text-slate-400">Es: 10 Pieghe, 5 Colori, ecc.</p>
            </div>
          )}
        </TabsContent>

        {/* TAB GIFT CARD */}
        <TabsContent value="regalo" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">Gift card e buoni regalo</p>
            <Button 
              size="sm" 
              onClick={() => {
                setNuovaCardForm({ ...nuovaCardForm, tipo_card: "regalo" });
                setShowNuovaCard(true);
              }}
              className="bg-pink-600 hover:bg-pink-700"
            >
              <Plus className="w-4 h-4 mr-1" /> Nuova Gift Card
            </Button>
          </div>

          {cardRegalo.length > 0 ? (
            <div className="space-y-3">
              {cardRegalo.map(card => (
                <div 
                  key={card.id}
                  className={`p-4 rounded-xl border-2 ${
                    card.stato === "attiva" ? "bg-pink-50 border-pink-300" : "bg-slate-50 border-slate-300"
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-white">
                        <Gift className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{card.nome}</p>
                        <p className="text-xs text-slate-500">Gift Card</p>
                        {card.operatore_nome && (
                          <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold" title="Operatore che ha venduto la card">
                            👤 {card.operatore_nome}
                          </span>
                        )}
                      </div>
                    </div>
                    {getStatoBadge(card.stato)}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">Saldo</span>
                    <span className={`font-bold text-2xl ${card.saldo > 0 ? "text-pink-600" : "text-slate-400"}`}>
                      €{card.saldo.toFixed(2)}
                    </span>
                  </div>

                  {card.saldo_iniziale > 0 && (
                    <Progress 
                      value={(card.saldo / card.saldo_iniziale) * 100} 
                      className="h-2 mt-2" 
                    />
                  )}

                  {card.data_scadenza && (
                    <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Scade il {format(parseISO(card.data_scadenza), "d MMM yyyy", { locale: it })}
                    </p>
                  )}

                  <div className="flex gap-2 mt-4 pt-3 border-t border-pink-200">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => { setSelectedCard(card); setShowStorico(true); }}
                      className="flex-1"
                    >
                      <History className="w-4 h-4 mr-1" /> Storico
                    </Button>
                    {cliente.telefono && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => inviaRemind(card)}
                        className="text-green-600 border-green-200 hover:bg-green-50"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-slate-50 rounded-xl">
              <Gift className="w-12 h-12 mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500">Nessuna Gift Card</p>
              <p className="text-xs text-slate-400">Crea buoni regalo per i clienti</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog Nuova Card (Trattamenti/Regalo) */}
      <Dialog open={showNuovaCard} onOpenChange={setShowNuovaCard}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {nuovaCardForm.tipo_card === "trattamenti" ? (
                <><Package className="w-5 h-5 text-purple-500" /> Nuovo Pacchetto Trattamenti</>
              ) : (
                <><Gift className="w-5 h-5 text-pink-500" /> Nuova Gift Card</>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {/* Bottoni Conferma in alto — sempre visibili */}
          <div className="flex gap-2 sticky top-0 z-10 bg-white py-2 -mt-2 shadow-sm border-b border-slate-100">
            <Button variant="outline" onClick={() => setShowNuovaCard(false)} className="flex-1">
              Annulla
            </Button>
            <Button 
              onClick={creaCardAvanzata} 
              disabled={!nuovaCardForm.operatore_id || !(parseFloat(nuovaCardForm.saldo_iniziale) > 0)}
              className={`flex-1 ${
                nuovaCardForm.tipo_card === "trattamenti" 
                  ? "bg-purple-600 hover:bg-purple-700" 
                  : "bg-pink-600 hover:bg-pink-700"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid="btn-conferma-cassa-card"
            >
              Incassa & Crea
            </Button>
          </div>
          
          <div className="space-y-4 pt-2">
            <div>
              <Label>Nome (opzionale)</Label>
              <Input 
                value={nuovaCardForm.nome}
                onChange={(e) => setNuovaCardForm({ ...nuovaCardForm, nome: e.target.value })}
                placeholder={nuovaCardForm.tipo_card === "trattamenti" ? "Es: Pacchetto 10 Pieghe" : "Es: Gift Card Natale"}
                className="mt-1"
              />
            </div>

            {nuovaCardForm.tipo_card === "trattamenti" && (
              <>
                <div>
                  <Label>Servizio</Label>
                  <Select 
                    value={nuovaCardForm.servizio_id}
                    onValueChange={(val) => setNuovaCardForm({ ...nuovaCardForm, servizio_id: val })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleziona servizio" />
                    </SelectTrigger>
                    <SelectContent>
                      {servizi.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.nome} - €{s.prezzo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Numero Trattamenti</Label>
                  <Input 
                    type="number"
                    value={nuovaCardForm.trattamenti_totali || ""}
                    onChange={(e) => setNuovaCardForm({ ...nuovaCardForm, trattamenti_totali: parseInt(e.target.value) || 0 })}
                    placeholder="Es: 10"
                    className="mt-1"
                  />
                </div>
              </>
            )}

            <div>
              <Label>Importo (€){nuovaCardForm.tipo_card === "trattamenti" ? " - Prezzo pacchetto" : ""}</Label>
              <Input 
                type="number"
                value={nuovaCardForm.saldo_iniziale || ""}
                onChange={(e) => setNuovaCardForm({ ...nuovaCardForm, saldo_iniziale: parseFloat(e.target.value) || 0 })}
                placeholder={nuovaCardForm.tipo_card === "trattamenti" ? "Es: 80" : "Es: 50"}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Data Scadenza (opzionale)</Label>
              <Input 
                type="date"
                value={nuovaCardForm.data_scadenza}
                onChange={(e) => setNuovaCardForm({ ...nuovaCardForm, data_scadenza: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Metodo Pagamento</Label>
              <Select 
                value={nuovaCardForm.metodo_pagamento}
                onValueChange={(val) => setNuovaCardForm({ ...nuovaCardForm, metodo_pagamento: val })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contanti">Contanti</SelectItem>
                  <SelectItem value="carta">Carta</SelectItem>
                  <SelectItem value="pos">POS</SelectItem>
                  <SelectItem value="bonifico">Bonifico</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Passaggio Cassa: operatore obbligatorio + riepilogo importo */}
            <div className="border-t pt-3 mt-2 space-y-3 bg-amber-50/60 -mx-6 px-6 py-3 rounded-b-md">
              <div className="flex items-center gap-2">
                <Wallet className="w-4 h-4 text-amber-600" />
                <Label className="font-semibold text-amber-700">Passaggio Cassa</Label>
              </div>
              <div>
                <Label className="text-xs">
                  Operatore <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={nuovaCardForm.operatore_id}
                  onValueChange={(val) => {
                    const op = operatori.find(o => o.id === val);
                    setNuovaCardForm({
                      ...nuovaCardForm,
                      operatore_id: val,
                      operatore_nome: op?.nome || ""
                    });
                  }}
                >
                  <SelectTrigger className="mt-1" data-testid="select-operatore-card">
                    <SelectValue placeholder="Seleziona operatore..." />
                  </SelectTrigger>
                  <SelectContent>
                    {operatori.length === 0 && (
                      <div className="px-2 py-1 text-xs text-slate-400">Nessun operatore disponibile</div>
                    )}
                    {operatori.map(op => (
                      <SelectItem key={op.id} value={op.id}>{op.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500 mt-1">
                  Obbligatorio: l&apos;incasso sarà attribuito a questo operatore nel report.
                </p>
              </div>
              <div className="flex items-center justify-between bg-white border border-amber-200 rounded-md px-3 py-2">
                <span className="text-sm text-slate-600">Totale da incassare:</span>
                <span className="text-lg font-bold text-amber-700" data-testid="totale-cassa-card">
                  €{(parseFloat(nuovaCardForm.saldo_iniziale) || 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Storico */}
      <Dialog open={showStorico} onOpenChange={setShowStorico}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-500" />
              Storico - {selectedCard?.nome}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCard && (
            <div className="space-y-2 pt-4">
              {selectedCard.movimenti && selectedCard.movimenti.length > 0 ? (
                selectedCard.movimenti
                  .slice()
                  .sort((a, b) => new Date(b.data) - new Date(a.data))
                  .map((mov, idx) => (
                    <div 
                      key={mov.id || idx}
                      className={`flex items-center gap-3 p-3 rounded-lg ${
                        mov.tipo === "ricarica" || mov.tipo === "creazione" ? "bg-green-50" : "bg-red-50"
                      }`}
                    >
                      {mov.tipo === "ricarica" || mov.tipo === "creazione" ? (
                        <ArrowUpCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <ArrowDownCircle className="w-5 h-5 text-red-600" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{mov.descrizione}</p>
                        <p className="text-xs text-slate-500">
                          {formatDate(mov.data)}
                          {mov.operatore_nome && (
                            <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-medium">
                              👤 {mov.operatore_nome}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="text-right">
                        {mov.importo > 0 && (
                          <span className={`font-bold ${
                            mov.tipo === "ricarica" || mov.tipo === "creazione" ? "text-green-600" : "text-red-600"
                          }`}>
                            {mov.tipo === "ricarica" || mov.tipo === "creazione" ? "+" : "-"}€{mov.importo.toFixed(2)}
                          </span>
                        )}
                        {mov.quantita > 0 && (
                          <span className="text-purple-600 font-medium block">
                            {mov.tipo === "utilizzo" ? "-" : "+"}{mov.quantita} tratt.
                          </span>
                        )}
                      </div>
                    </div>
                  ))
              ) : (
                <div className="text-center py-8 text-slate-400">
                  Nessun movimento
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
